import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import axios from 'axios';
import { Loader2, Check, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OAuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing');
  const [provider, setProvider] = useState('');

  const processCallback = useCallback(async () => {
    const state = searchParams.get('state');
    const code = searchParams.get('code') || 'simulated_auth_code';
    // Read and immediately clear provider from sessionStorage (minimize exposure window)
    const storedProvider = sessionStorage.getItem('oauth_provider') || '';
    sessionStorage.removeItem('oauth_provider');
    setProvider(storedProvider);

    if (!state || !storedProvider) {
      setStatus('error');
      setTimeout(() => navigate('/dashboard'), 3000);
      return;
    }

    try {
      await axios.post(`${API}/api/oauth/${storedProvider}/callback`,
        { state, code }, { withCredentials: true });
      setStatus('success');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (e) {
      console.error('OAuth callback error:', e);
      setStatus('error');
      setTimeout(() => navigate('/dashboard'), 3000);
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    processCallback();
  }, [processCallback]);

  return (
    <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-zinc-950' : 'bg-zinc-50'}`}>
      <div className={`text-center p-8 rounded-2xl ${isDark ? 'bg-zinc-900' : 'bg-white shadow-lg'}`} style={{ minWidth: 320 }}>
        {status === 'processing' && (
          <>
            <Loader2 size={36} className="animate-spin mx-auto mb-4" style={{ color: '#00D4FF' }} />
            <h2 className="text-lg font-bold mb-2">Connecting {provider}...</h2>
            <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>Exchanging authorization tokens</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: '#06D6A0' }}>
              <Check size={24} className="text-white" />
            </div>
            <h2 className="text-lg font-bold mb-2">Connected!</h2>
            <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>Redirecting to dashboard...</p>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertTriangle size={36} className="mx-auto mb-4 text-orange-500" />
            <h2 className="text-lg font-bold mb-2">Connection Issue</h2>
            <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>Redirecting back...</p>
          </>
        )}
      </div>
    </div>
  );
}
